import { TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useWindowLocation } from 'utils/windowLocation'

import { formatMoney } from '../../utils'
import { handleAmountChange } from '../../utils/reimbursement_utils'
import { useCreateReimbursement } from '../hooks/reimbursements/useCreateReimbursement'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'

type HSAReimbursementProps = {
  maxHSABalance: number;
}

type HSAReimbursementInput = {
  member_description: string
  amount: string
  type: 'HSA'
}

const HSAReimbursement = ({ maxHSABalance }: HSAReimbursementProps) => {
  const { assign } = useWindowLocation()

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting, isSubmitted }
  } = useForm<HSAReimbursementInput>({
    defaultValues: {
      type: 'HSA',
      amount: '0',
      member_description: ''
    },
  })

  const createReimbursementMutation = useCreateReimbursement()

  const onSubmit: SubmitHandler<HSAReimbursementInput> = async (data: HSAReimbursementInput) => {
    await createReimbursementMutation.mutateAsync(data)
      .then((response) => {
        assign(response.redirect_to)
      })
      .catch((error: Error) => {
        setOpenConfirmDialog(false)
        toast.error(error.message, { dismissible: true, icon: <TriangleAlert className='w-5 h-5 text-yellow-400' />, position: 'top-right' })
      })
  }

  const amount = watch('amount')

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {errors.type && <span className='field-error'>{errors.type.message}</span>}
      <div className='mt-4 space-y-2'>
        <Controller
          name='amount'
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => {
            return (
              <label className='label-default font-medium text-ds-text-secondary'>
                Amount
                <input
                  className='input-default mt-1'
                  type='text'
                  inputMode='numeric'
                  name='amount'
                  value={value}
                  onChange={(e) => handleAmountChange(e, onChange, maxHSABalance)}
                />
                <div className='text-xs text-gray-400 mt-1'>Your available balance is {formatMoney(maxHSABalance)}</div>
              </label>
            )
          }}
        />
      </div>
      <div className='mt-4 space-y-2'>
        <label className='label-default font-medium text-ds-text-secondary'>
          Notes
          <textarea
            className='input-default mt-1'
            {...register('member_description')}
          />
        </label>
      </div>

      <Button className='w-full mt-6' onClick={() => setOpenConfirmDialog(true)} type='button'>
        Submit
      </Button>
      <Dialog open={openConfirmDialog} onOpenChange={setOpenConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='h2'>Confirm your reimbursement details</DialogTitle>
            <DialogDescription asChild className='DialogDescription'>
              <div className='flex flex-col space-y-4 my-8 w-full text-gray-700'>
                <div className='flex justify-between'>
                  <div>HSA Balance</div>
                  <div>{formatMoney(maxHSABalance)}</div>
                </div>
                <div className='flex justify-between mt-2'>
                  <div>Reimbursement</div>
                  <div className='text-red-400'>-{formatMoney(parseFloat(amount))}</div>
                </div>
                <div className='flex justify-between border-t font-semibold'>
                  <div className='pt-4'>Remaining balance</div>
                  <div className='pt-4 font-semibold' data-testid='remaining-balance'>{formatMoney((maxHSABalance - parseFloat(amount)))}</div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Button
            className='submit'
            disabled={isSubmitting || isSubmitted}
            onClick={async () => {
              await handleSubmit(onSubmit)()
            }}
          >
            {isSubmitting || isSubmitted ? 'Submitting...' : ' Yes, submit my reimbursement'}
          </Button>
        </DialogContent>
      </Dialog>
    </form>

  )
}

export default HSAReimbursement
